export type InsurerDashboardTab =
  | 'clients'
  | 'analytics'
  | 'billing'
  | 'team'
  | 'settings';

export const INSURER_DASHBOARD_PATH = '/insurer-dashboard';
export const INSURER_ANALYTICS_PATH = '/analytics-dashboard';

const VALID_TABS: InsurerDashboardTab[] = [
  'clients',
  'analytics',
  'billing',
  'team',
  'settings',
];

const ORG_ADMIN_ONLY_TABS: InsurerDashboardTab[] = ['billing', 'team', 'settings'];

export function isOrgAdminOnlyInsurerTab(tab: InsurerDashboardTab): boolean {
  return ORG_ADMIN_ONLY_TABS.includes(tab);
}

export function insurerDashboardTabItems(
  isOrgAdmin: boolean
): { key: InsurerDashboardTab; label: string }[] {
  const base: { key: InsurerDashboardTab; label: string }[] = [
    { key: 'clients', label: 'Clients' },
    { key: 'analytics', label: 'Analytics' },
  ];
  if (!isOrgAdmin) {
    return base;
  }
  return [
    ...base,
    { key: 'billing', label: 'Credits & billing' },
    { key: 'team', label: 'Team' },
    { key: 'settings', label: 'Settings' },
  ];
}

/** Legacy `?tab=analytics` on insurer-dashboard redirects to the analytics page. */
export function insurerDashboardLegacyTabRedirect(search: URLSearchParams): string | null {
  if (search.get('tab') !== 'analytics') {
    return null;
  }
  return INSURER_ANALYTICS_PATH;
}

export function parseInsurerDashboardTab(
  search: URLSearchParams,
  isOrgAdmin: boolean,
  pathname: string = INSURER_DASHBOARD_PATH
): InsurerDashboardTab {
  if (pathname === INSURER_ANALYTICS_PATH) {
    return 'analytics';
  }

  const raw = search.get('tab');
  if (raw === 'analytics') {
    return 'analytics';
  }
  if (raw && VALID_TABS.includes(raw as InsurerDashboardTab)) {
    const tab = raw as InsurerDashboardTab;
    if (!isOrgAdmin && isOrgAdminOnlyInsurerTab(tab)) {
      return 'clients';
    }
    return tab;
  }
  return 'clients';
}

/** True when URL requests an admin-only tab but the user is not an org admin. */
export function insurerDashboardTabRequiresRedirect(
  search: URLSearchParams,
  isOrgAdmin: boolean
): boolean {
  const raw = search.get('tab');
  if (!raw || !VALID_TABS.includes(raw as InsurerDashboardTab)) {
    return false;
  }
  return !isOrgAdmin && isOrgAdminOnlyInsurerTab(raw as InsurerDashboardTab);
}

export function insurerDashboardTabHref(tab: InsurerDashboardTab): string {
  if (tab === 'clients') {
    return INSURER_DASHBOARD_PATH;
  }
  if (tab === 'analytics') {
    return INSURER_ANALYTICS_PATH;
  }
  return `${INSURER_DASHBOARD_PATH}?tab=${tab}`;
}

export function getInsurerDashboardTabLabel(
  tab: InsurerDashboardTab,
  isOrgAdmin: boolean
): string {
  const match = insurerDashboardTabItems(isOrgAdmin).find((item) => item.key === tab);
  return match?.label ?? 'Clients';
}

export function isInsurerDashboardArea(pathname: string): boolean {
  return (
    pathname === INSURER_DASHBOARD_PATH ||
    pathname === INSURER_ANALYTICS_PATH ||
    pathname.startsWith('/bulk-invite')
  );
}

export function isInsurerAnalyticsPage(pathname: string): boolean {
  return pathname === INSURER_ANALYTICS_PATH;
}
